import React, { useEffect, useState } from 'react';
import {
  Button,
  DialogContent,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import InputField from '../atoms/InputField';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import PinOutlinedIcon from '@mui/icons-material/PinOutlined';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import styles from './ClientDataDialogCommon.module.scss';
import BaseDialog from '../DialogWrappers/BaseDialog';
import ConfirmationDialog from '../DialogWrappers/ConfirmationDialog';
import {
  generateNewPin,
  getNewMember,
  isValidEmail,
  normalizeWhitespace,
} from '../../utils/utils';
import {
  createDashboardSubscription,
  getSubscriptionOptions,
} from '../../utils/dashboardUtils';
import { Product, UserType } from '../../constants';
import { useSessionData } from '../../providers';

import type { Member } from '../../types';

export interface AddClientDialogProps {
  gymMembers: Member[];
  onClose: () => void;
  onSubmit: (
    newMember: Member,
    trainerId?: string,
  ) => Promise<{
    success: boolean;
    emailAlreadyInUse?: boolean;
    pinAlreadyInUse?: boolean;
  }>;
  isOpen: boolean;
  trainers: { id: string; name: string }[];
  userType: UserType;
  draft: AddClientFormData | null;
  onDraftChange: React.Dispatch<React.SetStateAction<AddClientFormData | null>>;
}

const getInitialClientData = (userType: UserType, trainerId: string) => ({
  firstName: '',
  lastName: '',
  email: '',
  emailVerification: '',
  pin: '',
  trainerId,
  subscription: userType === UserType.Athlete ? Product.Programmed : '',
});

export type AddClientFormData = ReturnType<typeof getInitialClientData>;

const AddClientDialog: React.FC<AddClientDialogProps> = ({
  gymMembers,
  isOpen,
  onSubmit,
  onClose,
  trainers,
  userType,
  draft,
  onDraftChange,
}) => {
  const { loginTrainer, gym } = useSessionData();
  const enable5DigitPin =
    gym?.configuration?.enabledFeatures?.enable5DigitPin ?? false;
  const requiredPinLength = enable5DigitPin ? 5 : 4;

  const clientData =
    draft ?? getInitialClientData(userType, loginTrainer?.id || '');
  const setClientData: React.Dispatch<
    React.SetStateAction<AddClientFormData>
  > = (nextClientData) => {
    onDraftChange((previousDraft) => {
      const currentDraft =
        previousDraft ??
        getInitialClientData(userType, loginTrainer?.id || '');
      return typeof nextClientData === 'function'
        ? nextClientData(currentDraft)
        : nextClientData;
    });
  };

  const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] =
    useState<boolean>(false);
  const initialClientDataRef = React.useRef(getInitialClientData(userType, loginTrainer?.id || ''));

  const [firstNameError, setFirstNameError] = useState<boolean>(false);
  const [lastNameError, setLastNameError] = useState<boolean>(false);
  const [pinError, setPinError] = useState<boolean>(false);
  const [pinErrorMessage, setPinErrorMessage] = useState<string>('');
  const [emailError, setEmailError] = useState<boolean>(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>('');
  const [emailVerificationError, setEmailVerificationError] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || draft) {
      return;
    }

    let generatedPin = '';
    try {
      generatedPin = generateNewPin(gymMembers, {
        pinLength: enable5DigitPin ? 5 : 4,
      });
    } catch (error) {
      console.error('Error generating initial pin:', error);
      const friendlyMessage =
        error instanceof Error &&
          error.message.includes('Unable to generate a unique')
          ? 'All PIN combinations are currently in use. Please free up an existing PIN.'
          : 'An unexpected error occurred. Please try again.';
      setErrorMessage(friendlyMessage);
    }

    setClientData({
      ...getInitialClientData(userType, loginTrainer?.id || ''),
      pin: generatedPin,
    });
    initialClientDataRef.current = {
      ...getInitialClientData(userType, loginTrainer?.id || ''),
      pin: generatedPin,
    };
    setFirstNameError(false);
    setLastNameError(false);
    setPinError(false);
    setPinErrorMessage('');
    setEmailError(false);
    setEmailErrorMessage('');
    setEmailVerificationError(false);
    setLoading(false);
    setErrorMessage(null);
  }, [
    draft,
    enable5DigitPin,
    gymMembers,
    isOpen,
    loginTrainer?.id,
    setClientData,
    userType,
  ]);

  const hasChanges = () => {
    const initial = initialClientDataRef.current;
    if (!initial) return false;
    return (
      initial.firstName !== clientData.firstName ||
      initial.lastName !== clientData.lastName ||
      initial.email !== clientData.email ||
      (initial.pin || '') !== clientData.pin ||
      (initial.trainerId || '') !== clientData.trainerId ||
      (initial.subscription || '') !== clientData.subscription
    );
  };

  const handleRequestClose = () => {
    if (hasChanges()) {
      setIsCloseConfirmationOpen(true);
    } else {
      onClose();
    }
  };

  const confirmClose = () => {
    setIsCloseConfirmationOpen(false);
    onClose();
  };

  const handleSubmit = async () => {
    setErrorMessage(null);

    const firstName = normalizeWhitespace(clientData.firstName);
    const lastName = normalizeWhitespace(clientData.lastName);
    const pinTrimmed = clientData.pin.trim();
    const pinWrongLength = pinTrimmed.length !== requiredPinLength;
    const validationErrors = {
      firstName: !firstName,
      lastName: !lastName,
      email: !isValidEmail(clientData.email.trim()),
      emailVerification: clientData.email.trim() !== clientData.emailVerification.trim(),
      pin: pinWrongLength,
    };

    setFirstNameError(validationErrors.firstName);
    setLastNameError(validationErrors.lastName);
    setEmailError(validationErrors.email);
    setEmailErrorMessage(
      validationErrors.email ? 'Please enter a valid email address.' : '',
    );
    setEmailVerificationError(validationErrors.emailVerification);
    setPinError(validationErrors.pin);
    setPinErrorMessage(
      pinWrongLength ? `PIN must be ${requiredPinLength} digits` : '',
    );

    if (Object.values(validationErrors).some(Boolean)) return;

    setLoading(true);

    try {
      const newMember = getNewMember({
        phoneNumber: '',
        email: clientData.email.trim(),
        pin: clientData.pin.trim(),
        firstName,
        lastName,
        subscriptions: clientData.subscription
          ? [createDashboardSubscription(clientData.subscription as Product)]
          : [],
        records: {
          exercises: {},
        },
      });

      const result = await onSubmit(newMember, clientData.trainerId);

      if (result.success) {
        setClientData(getInitialClientData(userType, clientData.trainerId));
        onClose();
      } else if (result.emailAlreadyInUse) {
        setEmailError(true);
        setEmailErrorMessage('Email already in use');
      } else if (result.pinAlreadyInUse) {
        setPinError(true);
        setPinErrorMessage('PIN already in use');
      } else {
        setErrorMessage('An error occurred. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrorMessage('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <>
      <BaseDialog
        onClose={handleRequestClose}
        isOpen={isOpen}
        title={`Add ${userType === UserType.Client ? `A Client` : `An Athlete`}`}
        actions={
          <Button
            className={styles.submit}
            variant="outlined"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="primary" />
            ) : (
              <>
                <AddBoxOutlinedIcon color="primary" />
                <span className={styles.submitText}>{`Add ${userType}`}</span>
              </>
            )}
          </Button>
        }
      >
        <DialogContent className={styles.clientDataDialog}>
          <div className={styles.row}>
            <div className={styles.rowColumn}>
              <InputField
                icon={<PersonOutlineOutlinedIcon color="primary" />}
                title="First Name"
                inputName="firstName"
                autoComplete="firstName"
                value={clientData.firstName}
                onChange={(firstName) => {
                  setFirstNameError(false);
                  setClientData((prevClientData) => ({
                    ...prevClientData,
                    firstName,
                  }));
                }}
                error={firstNameError}
                validationMessage="Please enter a first name."
              />
            </div>
            <div className={styles.rowColumn}>
              <InputField
                icon={<PersonOutlineOutlinedIcon color="primary" />}
                title="Last Name"
                inputName="lastName"
                autoComplete="lastName"
                value={clientData.lastName}
                onChange={(lastName) => {
                  setLastNameError(false);
                  setClientData((prevClientData) => ({
                    ...prevClientData,
                    lastName,
                  }));
                }}
                error={lastNameError}
                validationMessage="Please enter a last name."
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.rowColumn}>
              <InputField
                icon={<MailOutlineRoundedIcon color="primary" />}
                title="Email"
                inputName="email"
                autoComplete="email"
                value={clientData.email}
                onChange={(email) => {
                  setEmailError(false);
                  setEmailErrorMessage('');
                  setEmailVerificationError(false);
                  setClientData((prevClientData) => ({
                    ...prevClientData,
                    email,
                  }));
                }}
                onBlur={() => {
                  const isInvalid = !isValidEmail(clientData.email);
                  setEmailError(isInvalid);
                  setEmailErrorMessage(
                    isInvalid ? 'Please enter a valid email address.' : '',
                  );
                }}
                error={emailError}
                validationMessage={emailErrorMessage}
              />
            </div>
            <div className={styles.rowColumn}>
              <InputField
                icon={<MailOutlineRoundedIcon color="primary" />}
                title="Email Verification"
                inputName="emailVerification"
                autoComplete="email"
                value={clientData.emailVerification}
                onChange={(emailVerification) => {
                  setEmailVerificationError(false);
                  setClientData((prevClientData) => ({
                    ...prevClientData,
                    emailVerification,
                  }));
                }}
                onBlur={() =>
                  setEmailVerificationError(
                    clientData.email !== clientData.emailVerification,
                  )
                }
                error={emailVerificationError}
                validationMessage="Emails do not match."
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.rowColumn}>
              <InputField
                icon={<PinOutlinedIcon color="primary" />}
                title="PIN"
                inputName="pin"
                autoComplete="off"
                value={clientData.pin}
                onChange={(pin) => {
                  setPinError(false);
                  setPinErrorMessage('');
                  setClientData((prevClientData) => ({
                    ...prevClientData,
                    pin: pin.replace(/\D/g, '').slice(0, requiredPinLength),
                  }));
                }}
                error={pinError}
                validationMessage={pinErrorMessage || `PIN must be ${requiredPinLength} digits.`}
              />
            </div>
            <div className={styles.rowColumn}>
              <InputField
                icon={<PersonOutlineOutlinedIcon color="primary" />}
                title="Trainer"
                inputName="trainerId"
                value={clientData.trainerId}
                options={trainers.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
                onChange={(trainerId) => {
                  setClientData((prevClientData) => ({
                    ...prevClientData,
                    trainerId,
                  }));
                }}
              />
            </div>
          </div>
          {userType !== UserType.Athlete && (
            <div className={styles.row}>
              <div className={styles.rowColumn}>
                <InputField
                  icon={<SupervisorAccountOutlinedIcon color="primary" />}
                  title="Subscription"
                  inputName="subscription"
                  value={clientData.subscription}
                  options={getSubscriptionOptions()}
                  onChange={(subscription) => {
                    setClientData((prevClientData) => ({
                      ...prevClientData,
                      subscription,
                    }));
                  }}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </BaseDialog>
      <ConfirmationDialog
        isOpen={isCloseConfirmationOpen}
        onCancel={() => setIsCloseConfirmationOpen(false)}
        onSubmit={() => confirmClose()}
        title="Discard changes?"
        text={`You have unsaved changes. Do you want to discard them?`}
        submitText="Discard"
        cancelText="Keep Editing"
      />
      </>
      {errorMessage && (
        <Snackbar
          open={true}
          autoHideDuration={6000}
          onClose={() => setErrorMessage(null)}
        >
          <Alert onClose={() => setErrorMessage(null)} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default AddClientDialog;
